import { useEffect, useState } from 'react';
import { holidaysAPI } from '../api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Calendar, Plus, Edit, Trash2, Clock, RefreshCw, Loader2, Globe
} from 'lucide-react';

export const HolidaySettingsPage = () => {
  const { hasRole } = useAuth();
  const [loading, setLoading] = useState(true);
  const [holidays, setHolidays] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Dialog states
  const [holidayDialog, setHolidayDialog] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [holidayToDelete, setHolidayToDelete] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    date: '',
    is_recurring: true,
    affects_sla: true,
    country: 'ALL'
  });

  useEffect(() => {
    fetchHolidays();
  }, [selectedYear]);

  const fetchHolidays = async () => {
    try {
      setLoading(true);
      const res = await holidaysAPI.getAll(selectedYear);
      setHolidays(res.data || []);
    } catch (error) {
      console.error('Failed to fetch holidays:', error);
      toast.error('Failed to load holidays');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (holiday = null) => {
    if (holiday) {
      setEditingHoliday(holiday);
      // Parse date as UTC to avoid timezone shift
      const holidayDate = new Date(holiday.date);
      const localDate = new Date(holidayDate.getTime() + holidayDate.getTimezoneOffset() * 60000);
      setFormData({
        name: holiday.name,
        date: format(localDate, 'yyyy-MM-dd'),
        is_recurring: holiday.is_recurring,
        affects_sla: holiday.affects_sla,
        country: holiday.country || 'ALL'
      });
    } else {
      setEditingHoliday(null);
      setFormData({
        name: '',
        date: '',
        is_recurring: true,
        affects_sla: true,
        country: 'ALL'
      });
    }
    setHolidayDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Parse date as local date without timezone conversion
      const [year, month, day] = formData.date.split('-').map(Number);
      const localDate = new Date(year, month - 1, day, 12, 0, 0); // Set to noon to avoid timezone issues
      
      const data = {
        ...formData,
        date: localDate.toISOString()
      };
      
      if (editingHoliday) {
        await holidaysAPI.update(editingHoliday.id, data);
        toast.success('Holiday updated');
      } else {
        await holidaysAPI.create(data);
        toast.success('Holiday created');
      }
      
      setHolidayDialog(false);
      fetchHolidays();
    } catch (error) {
      toast.error(editingHoliday ? 'Failed to update holiday' : 'Failed to create holiday');
    }
  };

  const handleDelete = async () => {
    if (!holidayToDelete) return;
    try {
      await holidaysAPI.delete(holidayToDelete.id);
      toast.success('Holiday deleted');
      setDeleteDialog(false);
      setHolidayToDelete(null);
      fetchHolidays();
    } catch (error) {
      toast.error('Failed to delete holiday');
    }
  };

  const years = [];
  const currentYear = new Date().getFullYear();
  for (let y = currentYear - 1; y <= currentYear + 2; y++) {
    years.push(y);
  }

  if (!hasRole(['admin'])) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Card className="p-8 text-center">
          <Calendar className="h-12 w-12 mx-auto text-slate-300 mb-4" />
          <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
          <p className="text-muted-foreground">
            Only administrators can manage holidays.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="holiday-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="h-6 w-6" />
            Holiday Calendar
          </h1>
          <p className="text-muted-foreground">
            Manage statutory holidays that affect SLA calculations
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
            <SelectTrigger className="w-[120px]" data-testid="year-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={() => handleOpenDialog()} data-testid="add-holiday-btn">
            <Plus className="h-4 w-4 mr-2" />
            Add Holiday
          </Button>
        </div>
      </div>

      {/* Info Card */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Clock className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-900">How holidays affect SLA</p>
              <p className="text-sm text-blue-700">
                When a holiday is set to "Affects SLA", ticket response and resolution timers will pause for the entire day. 
                Recurring holidays automatically apply to the same date each year.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Holidays List */}
      <Card>
        <CardHeader>
          <CardTitle>Holidays for {selectedYear}</CardTitle>
          <CardDescription>
            {holidays.length} holiday{holidays.length !== 1 ? 's' : ''} configured
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : holidays.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p>No holidays configured for {selectedYear}</p>
              <p className="text-sm">Click "Add Holiday" to get started</p>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              <div className="space-y-3">
                {holidays.map((holiday) => {
                  // Parse date as UTC to avoid timezone shift
                  const holidayDate = new Date(holiday.date);
                  const displayDate = new Date(holidayDate.getTime() + holidayDate.getTimezoneOffset() * 60000);
                  
                  return (
                  <div
                    key={holiday.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 transition-colors"
                    data-testid={`holiday-${holiday.id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 bg-primary/10 rounded-lg flex flex-col items-center justify-center">
                        <span className="text-xs font-medium text-primary uppercase">
                          {format(displayDate, 'MMM')}
                        </span>
                        <span className="text-2xl font-bold text-primary">
                          {format(displayDate, 'd')}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{holiday.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {holiday.is_recurring && (
                            <Badge variant="outline" className="text-xs">
                              <RefreshCw className="h-3 w-3 mr-1" />
                              Recurring
                            </Badge>
                          )}
                          {holiday.affects_sla && (
                            <Badge className="bg-amber-100 text-amber-700 text-xs">
                              <Clock className="h-3 w-3 mr-1" />
                              Pauses SLA
                            </Badge>
                          )}
                          {holiday.country !== 'ALL' && (
                            <Badge variant="secondary" className="text-xs">
                              <Globe className="h-3 w-3 mr-1" />
                              {holiday.country}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => handleOpenDialog(holiday)}
                        data-testid={`edit-holiday-${holiday.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        onClick={() => {
                          setHolidayToDelete(holiday);
                          setDeleteDialog(true);
                        }}
                        data-testid={`delete-holiday-${holiday.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Common Holidays Suggestions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Quick Add Common Holidays</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {[
              { name: "New Year's Day", month: 1, day: 1 },
              { name: "Christmas Day", month: 12, day: 25 },
              { name: "Boxing Day", month: 12, day: 26 },
              { name: "Canada Day", month: 7, day: 1 },
              { name: "Labour Day", month: 9, day: 1 },
              { name: "Thanksgiving", month: 10, day: 14 },
              { name: "Victoria Day", month: 5, day: 20 },
              { name: "Remembrance Day", month: 11, day: 11 }
            ].map((suggestion) => (
              <Button
                key={suggestion.name}
                variant="outline"
                size="sm"
                onClick={() => {
                  setFormData({
                    name: suggestion.name,
                    date: `${selectedYear}-${String(suggestion.month).padStart(2, '0')}-${String(suggestion.day).padStart(2, '0')}`,
                    is_recurring: true,
                    affects_sla: true,
                    country: 'ALL'
                  });
                  setEditingHoliday(null);
                  setHolidayDialog(true);
                }}
              >
                <Plus className="h-3 w-3 mr-1" />
                {suggestion.name}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Holiday Dialog */}
      <Dialog open={holidayDialog} onOpenChange={setHolidayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingHoliday ? 'Edit Holiday' : 'Add Holiday'}</DialogTitle>
            <DialogDescription>
              {editingHoliday ? 'Update the holiday details' : 'Add a new statutory holiday'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Holiday Name</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="e.g., Christmas Day"
                required
                data-testid="holiday-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData(p => ({ ...p, date: e.target.value }))}
                required
                data-testid="holiday-date-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Country/Region</Label>
              <Select 
                value={formData.country}
                onValueChange={(v) => setFormData(p => ({ ...p, country: v }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">All Regions</SelectItem>
                  <SelectItem value="CA">Canada</SelectItem>
                  <SelectItem value="US">United States</SelectItem>
                  <SelectItem value="UK">United Kingdom</SelectItem>
                  <SelectItem value="AU">Australia</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
              <div>
                <p className="font-medium text-sm">Recurring Annually</p>
                <p className="text-xs text-muted-foreground">
                  Holiday repeats on the same date each year
                </p>
              </div>
              <Switch
                checked={formData.is_recurring}
                onCheckedChange={(v) => setFormData(p => ({ ...p, is_recurring: v }))}
                data-testid="recurring-toggle"
              />
            </div>

            <div className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
              <div>
                <p className="font-medium text-sm text-amber-900">Affects SLA Timers</p>
                <p className="text-xs text-amber-700">
                  Response and resolution timers pause on this day
                </p>
              </div>
              <Switch
                checked={formData.affects_sla}
                onCheckedChange={(v) => setFormData(p => ({ ...p, affects_sla: v }))}
                data-testid="affects-sla-toggle"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setHolidayDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="save-holiday-btn">
                {editingHoliday ? 'Update' : 'Create'} Holiday
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Holiday</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{holidayToDelete?.name}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default HolidaySettingsPage;
